import { Currency, formatToDollar, resolvePeriodProfitability } from "@inrev/common";
import { Option } from "@inrev/core";
import { formatEmpty, formatRatio } from "../../../utils";
import type { FinancialPeriodBundle, FinancialsMetrics } from "../hooks/useFinancialsMetrics";

const abbreviatedDollar = (value: Option<number>): string =>
	formatEmpty(formatToDollar(value, { abbreviate: true, accounting: true }));

const profitabilityValue = (bundle?: FinancialPeriodBundle): string => {
	const profitability = resolvePeriodProfitability(
		bundle && {
			isManual: bundle.isManual,
			profitable: bundle.profitable,
			netProfit: bundle.incomeStatement?.view?.netProfit.allowed,
		},
	);
	return profitability.kind === "binary"
		? formatEmpty(
				profitability.profitable === null ? null : profitability.profitable ? "Yes" : "No",
			)
		: abbreviatedDollar(profitability.netProfit);
};

export const getNetIncomeLabel = (bundle?: FinancialPeriodBundle): string =>
	resolvePeriodProfitability(bundle && { isManual: bundle.isManual }).kind === "binary"
		? "Profitable"
		: "Net Income";

export const getProfitabilityYTDValue = (recentYTD?: FinancialPeriodBundle): string =>
	profitabilityValue(recentYTD);

export const getNetIncomeFYEValue = (recentFYE?: FinancialPeriodBundle): string =>
	profitabilityValue(recentFYE);

export const getRevenueFYEValue = (recentFYE?: FinancialPeriodBundle): string =>
	abbreviatedDollar(recentFYE?.incomeStatement?.view?.revenue.total.allowed);

export const getEquityFYEValue = (recentFYE?: FinancialPeriodBundle): string =>
	abbreviatedDollar(recentFYE?.balanceSheet?.view?.equity.total.allowed);

export const computeWorkingCapital = (bundle?: FinancialPeriodBundle): Option<Currency> => {
	const currentAssets = bundle?.balanceSheet?.view?.assets.current.total.allowed;
	const currentLiabilities = bundle?.balanceSheet?.view?.liabilities.current.total.allowed;
	return Option.doWith(
		{ currentAssets, currentLiabilities },
		({ currentAssets, currentLiabilities }) => Currency.subtract(currentAssets, currentLiabilities),
	);
};

export const getWorkingCapitalFYEValue = (recentFYE?: FinancialPeriodBundle): string =>
	abbreviatedDollar(computeWorkingCapital(recentFYE));

export const getCurrentRatioFYEValue = (recentFYE?: FinancialPeriodBundle): string => {
	const currentAssets =
		recentFYE?.balanceSheet?.view?.assets.current.total.allowed ?? Currency.ZERO;
	const currentLiabilities =
		recentFYE?.balanceSheet?.view?.liabilities.current.total.allowed ?? Currency.ZERO;
	return formatRatio(currentAssets, currentLiabilities, "x");
};

export const getDebtEquityValue = (recentFYE?: FinancialPeriodBundle): string => {
	const liabilities = recentFYE?.balanceSheet?.view?.liabilities;
	const equity = recentFYE?.balanceSheet?.view?.equity.total.allowed;

	return formatEmpty(
		Option.doWith({ liabilities, equity }, ({ liabilities, equity }) => {
			const totalDebt = Currency.sum([
				liabilities.current.blocBalance.total.allowed ?? Currency.ZERO,
				liabilities.current.currentPortionOfLtd.total.allowed ?? Currency.ZERO,
				liabilities.nonCurrent.ltdBankTermLoan.total.allowed ?? Currency.ZERO,
				liabilities.nonCurrent.ltdRealEstate.total.allowed ?? Currency.ZERO,
				liabilities.nonCurrent.ltdEquipment.total.allowed ?? Currency.ZERO,
				liabilities.nonCurrent.ltdOther.total.allowed ?? Currency.ZERO,
			]);

			return formatRatio(totalDebt, equity);
		}),
	);
};

export const getBLOCValue = (recentFYE?: FinancialPeriodBundle): string => {
	const usage = recentFYE?.balanceSheet?.view?.liabilities.current.blocBalance.total.allowed;
	return `${abbreviatedDollar(usage)} / ${abbreviatedDollar(recentFYE?.blocSize)}`;
};

export type FinancialMetricStripItem = {
	id: string;
	label: string;
	value: string;
};

export type FinancialMetricStripGroups = {
	ytd: FinancialMetricStripItem[];
	fye: FinancialMetricStripItem[];
};

export const buildFinancialMetricStripItems = ({
	recentFYE,
	recentYTD,
}: FinancialsMetrics): FinancialMetricStripGroups => ({
	ytd: [
		{
			id: "profitability-ytd",
			label: getNetIncomeLabel(recentYTD),
			value: getProfitabilityYTDValue(recentYTD),
		},
	],
	fye: [
		{ id: "revenue-fye", label: "Revenue", value: getRevenueFYEValue(recentFYE) },
		{
			id: "net-income-fye",
			label: getNetIncomeLabel(recentFYE),
			value: getNetIncomeFYEValue(recentFYE),
		},
		{
			id: "working-capital-fye",
			label: "WC",
			value: getWorkingCapitalFYEValue(recentFYE),
		},
		{
			id: "current-ratio-fye",
			label: "Curr. Ratio",
			value: getCurrentRatioFYEValue(recentFYE),
		},
		{ id: "equity-fye", label: "Equity", value: getEquityFYEValue(recentFYE) },
		{ id: "debt-equity", label: "Debt:Equity", value: getDebtEquityValue(recentFYE) },
		{ id: "bloc", label: "BLOC", value: getBLOCValue(recentFYE) },
	],
});
